import { sectionRenderer } from '@theme/section-renderer';

class SearchResults extends HTMLElement {
  constructor() {
    super();
    this.currentViewType = 'products';
    this.viewButtons = [];
    this.viewContainers = [];
    this.initialPage = 1;
  }

  connectedCallback() {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    
    this.initializeViewType();
    this.setupViewButtons();
    this.setupViewContainers();
    this.setupLoadMoreButtons();
    this.addEventListener('page-loaded', this.handlePageLoaded);
    this.initialPage = 1;
    
    const buttonsContainer = this.querySelector('[data-view-buttons]');
    if (buttonsContainer) {
      const hasProducts = parseInt(this.dataset.productsCount || '0') > 0;
      const hasArticles = parseInt(this.dataset.articlesCount || '0') > 0;
      const hasPages = parseInt(this.dataset.pagesCount || '0') > 0;
      
      this.#updateButtonVisibility(buttonsContainer, 'products', hasProducts);
      this.#updateButtonVisibility(buttonsContainer, 'articles', hasArticles);
      this.#updateButtonVisibility(buttonsContainer, 'pages', hasPages);
    }
    
    this.extractArticlesPagesFromCurrentPage();
    this.checkPage1ForTypes().then(() => {
      this.updateViewButtonsFromCurrentPage();
    });
    
    const urlParams = new URLSearchParams(window.location.search);
    const viewTypeParam = urlParams.get('result_type');
    if (viewTypeParam && ['products', 'articles', 'pages'].includes(viewTypeParam)) {
      this.switchView(viewTypeParam, false);
    }
  }
  
  handlePageLoaded = (event) => {
    const { fetchedElement, page } = event.detail;
    
    if (!fetchedElement) {
      return;
    }
    
    const fetchedArticlesView = fetchedElement.querySelector('[data-view="articles"]');
    if (fetchedArticlesView) {
      const fetchedArticlesList = fetchedArticlesView.querySelector('[data-articles-list]');
      if (fetchedArticlesList && fetchedArticlesList.children.length > 0) {
        const currentArticlesView = this.viewContainers.articles;
        if (currentArticlesView) {
          let currentArticlesList = currentArticlesView.querySelector('[data-articles-list]');
          
          if (!currentArticlesList) {
            const listElement = document.createElement('ul');
            listElement.className = 'search-results__articles-list list-unstyled';
            listElement.setAttribute('data-articles-list', '');
            currentArticlesView.insertBefore(listElement, currentArticlesView.firstChild);
            currentArticlesList = listElement;
          }
          
          const articlesToAppend = Array.from(fetchedArticlesList.children).map(item => {
            const cloned = item.cloneNode(true);
            cloned.setAttribute('data-loaded-from-page', page.toString());
            return cloned;
          });
          
          const existingArticleIds = new Set(
            Array.from(currentArticlesList.children).map(li => {
              const link = li.querySelector('a');
              return link ? link.getAttribute('href') : null;
            }).filter(Boolean)
          );
          
          let appendedCount = 0;
          articlesToAppend.forEach(item => {
            const link = item.querySelector('a');
            const articleId = link ? link.getAttribute('href') : null;
            if (articleId && !existingArticleIds.has(articleId)) {
              currentArticlesList.appendChild(item);
              existingArticleIds.add(articleId);
              appendedCount++;
            }
          });
          
          if (appendedCount > 0) {
            const currentCount = parseInt(this.dataset.articlesCount || '0');
            this.dataset.articlesCount = (currentCount + appendedCount).toString();
          }
        }
      }
    }
    
    const fetchedPagesView = fetchedElement.querySelector('[data-view="pages"]');
    if (fetchedPagesView) {
      const fetchedPagesList = fetchedPagesView.querySelector('[data-pages-list]');
      if (fetchedPagesList && fetchedPagesList.children.length > 0) {
        const currentPagesView = this.viewContainers.pages;
        if (currentPagesView) {
          let currentPagesList = currentPagesView.querySelector('[data-pages-list]');
          
          if (!currentPagesList) {
            const listElement = document.createElement('ul');
            listElement.className = 'search-results__pages-list list-unstyled';
            listElement.setAttribute('data-pages-list', '');
            currentPagesView.insertBefore(listElement, currentPagesView.firstChild);
            currentPagesList = listElement;
          }
          
          const pagesToAppend = Array.from(fetchedPagesList.children).map(item => {
            const cloned = item.cloneNode(true);
            cloned.setAttribute('data-loaded-from-page', page.toString());
            return cloned;
          });
          
          const existingPageIds = new Set(
            Array.from(currentPagesList.children).map(li => {
              const link = li.querySelector('a');
              return link ? link.getAttribute('href') : null;
            }).filter(Boolean)
          );
          
          let appendedCount = 0;
          pagesToAppend.forEach(item => {
            const link = item.querySelector('a');
            const pageId = link ? link.getAttribute('href') : null;
            if (pageId && !existingPageIds.has(pageId)) {
              currentPagesList.appendChild(item);
              existingPageIds.add(pageId);
              appendedCount++;
            }
          });
          
          if (appendedCount > 0) {
            const currentCount = parseInt(this.dataset.pagesCount || '0');
            this.dataset.pagesCount = (currentCount + appendedCount).toString();
          }
        }
      }
    }
    
    this.updateViewButtonsFromFetchedPage(fetchedElement);
    this.updateViewButtonsFromCurrentPage();
  }

  initializeViewType() {
    const urlParams = new URLSearchParams(window.location.search);
    const viewTypeParam = urlParams.get('result_type');
    
    if (viewTypeParam && ['products', 'articles', 'pages'].includes(viewTypeParam)) {
      this.currentViewType = viewTypeParam;
    } else {
      const productsView = this.querySelector('[data-view="products"]');
      const articlesView = this.querySelector('[data-view="articles"]');
      const pagesView = this.querySelector('[data-view="pages"]');
      
      if (productsView && !productsView.hasAttribute('hidden')) {
        this.currentViewType = 'products';
      } else if (articlesView && !articlesView.hasAttribute('hidden')) {
        this.currentViewType = 'articles';
      } else if (pagesView && !pagesView.hasAttribute('hidden')) {
        this.currentViewType = 'pages';
      }
    }
  }

  setupViewButtons() {
    this.viewButtons = Array.from(
      this.querySelectorAll('.search-results__view-button')
    );
    
    this.viewButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        const viewType = e.currentTarget.dataset.viewType;
        this.switchView(viewType);
      });
    });
  }

  setupViewContainers() {
    this.viewContainers = {
      products: this.querySelector('[data-view="products"]'),
      articles: this.querySelector('[data-view="articles"]'),
      pages: this.querySelector('[data-view="pages"]'),
    };
    
    this.articlesCount = parseInt(this.dataset.articlesCount || '0');
    this.pagesCount = parseInt(this.dataset.pagesCount || '0');
    
    this.switchView(this.currentViewType, false);
    this.updateViewButtonsFromCurrentPage();
    // Update count displays on initial load
    this.updateCountDisplays();
  }

  updateViewButtonsFromCurrentPage() {
    const buttonsContainer = this.querySelector('[data-view-buttons]');
    if (!buttonsContainer) {
      return;
    }
    
    // Check actual DOM elements - tabs only show when content is loaded
    const productsView = this.querySelector('[data-view="products"]');
    const hasProducts = productsView && (
      productsView.querySelector('.product-grid__item') !== null ||
      productsView.querySelector('results-list') !== null ||
      productsView.querySelector('results-list .product-grid__item') !== null
    );
    
    const hasArticles = this.querySelector('[data-view="articles"] [data-articles-list] li') !== null ||
                        this.querySelector('[data-view="articles"] .search-results__article-item') !== null;
    const hasPages = this.querySelector('[data-view="pages"] [data-pages-list] li') !== null ||
                     this.querySelector('[data-view="pages"] .search-results__page-item') !== null;
    
    this.#updateButtonVisibility(buttonsContainer, 'products', !!hasProducts);
    this.#updateButtonVisibility(buttonsContainer, 'articles', hasArticles);
    this.#updateButtonVisibility(buttonsContainer, 'pages', hasPages);
  }

  #updateButtonVisibility(buttonsContainer, viewType, shouldShow) {
    let button = buttonsContainer.querySelector(`[data-view-type="${viewType}"]`);
    
    if (shouldShow) {
      if (!button) {
        button = this.#createViewButton(viewType, buttonsContainer);
      }
      if (button) {
      button.style.display = '';
      button.removeAttribute('hidden');
      }
    } else {
      if (button) {
        button.style.display = 'none';
        button.setAttribute('hidden', '');
      }
    }
  }

  #createViewButton(viewType, buttonsContainer) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'custom-button custom-button--secondary search-results__view-button';
    button.setAttribute('data-view-type', viewType);
    button.setAttribute('aria-pressed', 'false');
    
    const translations = window.theme?.strings?.search || {};
    const buttonText = {
      products: translations.view_products || 'Products',
      articles: translations.view_articles || 'Blog',
      pages: translations.view_pages || 'Pages'
    };
    button.textContent = buttonText[viewType] || `View ${viewType}`;
    
    button.addEventListener('click', () => {
      this.switchView(viewType, true);
    });
    
    const existingButtons = Array.from(buttonsContainer.querySelectorAll('[data-view-type]'));
    const order = ['products', 'articles', 'pages'];
    const currentIndex = order.indexOf(viewType);
    
    if (existingButtons.length === 0) {
      buttonsContainer.appendChild(button);
    } else {
      let insertBefore = null;
      for (let i = currentIndex + 1; i < order.length; i++) {
        const nextButton = buttonsContainer.querySelector(`[data-view-type="${order[i]}"]`);
        if (nextButton) {
          insertBefore = nextButton;
          break;
        }
      }
      if (insertBefore) {
        buttonsContainer.insertBefore(button, insertBefore);
      } else {
        buttonsContainer.appendChild(button);
      }
    }
    
    return button;
  }

  async loadPreviousPages(currentPage) {
    const currentViewType = this.currentViewType;
    const viewContainer = this.viewContainers[currentViewType];
    
    if (!viewContainer) {
      return;
    }
    
    let listSelector;
    let currentList;
    
    if (currentViewType === 'products') {
      const resultsList = viewContainer.querySelector('results-list');
      if (!resultsList) {
        return;
      }
      const grid = resultsList.querySelector('[role="list"]') || resultsList.querySelector('.product-grid');
      if (!grid) {
        return;
      }
      currentList = grid;
      listSelector = '.product-grid__item';
    } else if (currentViewType === 'articles') {
      listSelector = '[data-articles-list]';
      currentList = viewContainer.querySelector(listSelector);
    } else if (currentViewType === 'pages') {
      listSelector = '[data-pages-list]';
      currentList = viewContainer.querySelector(listSelector);
    } else {
      return;
    }
    
    if (!currentList) {
      return;
    }
    
    try {
      const pagesToLoad = currentPage - 1;
      const itemsToPrepend = [];
      
      for (let page = 1; page <= pagesToLoad; page++) {
          const url = new URL(window.location.href);
        url.searchParams.delete('page');
          url.searchParams.set('page', page.toString());
          url.searchParams.delete('type');
        
        const sectionHTML = await sectionRenderer.getSectionHTML(
          this.dataset.sectionId,
          true,
          url
        );
        
        if (!sectionHTML) {
          continue;
        }
        
        const parser = new DOMParser();
        const doc = parser.parseFromString(sectionHTML, 'text/html');
        const fetchedElement = doc.querySelector('search-results');
        
        if (fetchedElement) {
          const fetchedViewContainer = fetchedElement.querySelector(`[data-view="${currentViewType}"]`);
          
          if (fetchedViewContainer) {
            let fetchedList;
            if (currentViewType === 'products') {
              const fetchedResultsList = fetchedViewContainer.querySelector('results-list');
              if (fetchedResultsList) {
                fetchedList = fetchedResultsList.querySelector('[role="list"]') || fetchedResultsList.querySelector('.product-grid');
              }
            } else {
              fetchedList = fetchedViewContainer.querySelector(listSelector);
            }
            
            if (fetchedList) {
              const items = Array.from(fetchedList.children);
              itemsToPrepend.unshift(...items);
            }
          }
        }
      }
      
      if (itemsToPrepend.length === 0) {
        return;
      }
      
      if (currentViewType === 'products') {
        const existingProductIds = new Set(
          Array.from(
            currentList.querySelectorAll('.product-grid__item[data-product-id]')
          ).map(item => item.dataset.productId)
        );
        
        const uniqueItemsToPrepend = [];
        itemsToPrepend.forEach(item => {
          const productId = item.dataset.productId;
          if (productId && !existingProductIds.has(productId)) {
            existingProductIds.add(productId);
            uniqueItemsToPrepend.push(item);
          } else if (!productId) {
            uniqueItemsToPrepend.push(item);
          }
        });
        
        uniqueItemsToPrepend.forEach((item) => {
        const clonedItem = item.cloneNode(true);
        currentList.insertBefore(clonedItem, currentList.firstChild);
      });
      } else {
        itemsToPrepend.forEach((item) => {
          const clonedItem = item.cloneNode(true);
          currentList.insertBefore(clonedItem, currentList.firstChild);
        });
      }
      
      if (currentViewType === 'products') {
        const resultsList = viewContainer.querySelector('results-list');
        if (resultsList) {
          const grid = resultsList.querySelector('[role="list"]') || resultsList.querySelector('.product-grid');
          if (grid) {
            const totalResults = parseInt(this.dataset.totalResults || '0');
            grid.dataset.totalProducts = totalResults.toString();
            
            setTimeout(() => {
              requestAnimationFrame(() => {
                if (resultsList._updateProductCount) {
                  resultsList._updateProductCount();
                }
              });
            }, 100);
          }
        }
      } else {
        const countElement = viewContainer.querySelector('.search-results__count');
        if (countElement) {
          const totalCount = currentList.children.length;
          const totalResults = parseInt(this.dataset.totalResults || '0');
          countElement.textContent = this.getTranslation('search.count', {
            current: totalCount,
            total: totalResults
          });
        }
      }
    } catch (error) {
      console.error('[SearchResults] Error loading previous pages:', {
        error,
        currentPage,
        currentViewType,
        sectionId: this.dataset.sectionId
      });
    }
  }

  extractArticlesPagesFromCurrentPage() {
    const pageToTag = this.initialPage || 1;
    
    const articlesView = this.viewContainers.articles;
    if (articlesView) {
      const articlesList = articlesView.querySelector('[data-articles-list]');
      if (articlesList && articlesList.children.length > 0) {
        Array.from(articlesList.children).forEach(item => {
          if (!item.getAttribute('data-loaded-from-page')) {
            item.setAttribute('data-loaded-from-page', pageToTag.toString());
          }
        });
      }
    }
    
    const pagesView = this.viewContainers.pages;
    if (pagesView) {
      const pagesList = pagesView.querySelector('[data-pages-list]');
      if (pagesList && pagesList.children.length > 0) {
        Array.from(pagesList.children).forEach(item => {
          if (!item.getAttribute('data-loaded-from-page')) {
            item.setAttribute('data-loaded-from-page', pageToTag.toString());
          }
        });
      }
    }
  }

  async checkPage1ForTypes() {
    // Only check page 1 for types - don't load content from other pages
    // Tabs will appear when user clicks "Load more" and content is actually loaded
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      url.searchParams.set('page', '1');
      url.searchParams.delete('type');
      
      const sectionHTML = await sectionRenderer.getSectionHTML(this.dataset.sectionId, true, url);
      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHTML, 'text/html');
      const fetchedElement = doc.querySelector('search-results');
      
      if (fetchedElement) {
        // Only update buttons from page 1 - don't load content
        this.updateViewButtonsFromFetchedPage(fetchedElement);
        this.updateViewButtonsFromCurrentPage();
      }
    } catch (error) {
      console.error('[SearchResults] Error checking page 1 for types:', {
        error,
        sectionId: this.dataset.sectionId,
        totalPages: this.dataset.totalPages
      });
    }
  }

  updateViewButtonsFromFetchedPage(fetchedElement) {
    const buttonsContainer = this.querySelector('[data-view-buttons]');
    if (!buttonsContainer) {
      return;
    }
    
    // Check fetched page for types
    const fetchedProductsView = fetchedElement.querySelector('[data-view="products"]');
    const fetchedHasProducts = fetchedProductsView && (
      fetchedProductsView.querySelector('.product-grid__item') !== null ||
      fetchedProductsView.querySelector('results-list') !== null ||
      fetchedProductsView.querySelector('results-list .product-grid__item') !== null
    );
    
    const fetchedHasArticles = fetchedElement.querySelector('[data-view="articles"] [data-articles-list] li') !== null ||
                                fetchedElement.querySelector('[data-view="articles"] .search-results__article-item') !== null;
    const fetchedHasPages = fetchedElement.querySelector('[data-view="pages"] [data-pages-list] li') !== null ||
                             fetchedElement.querySelector('[data-view="pages"] .search-results__page-item') !== null;
    
    // Check current DOM for types
    const currentProductsView = this.querySelector('[data-view="products"]');
    const currentHasProducts = currentProductsView && (
      currentProductsView.querySelector('.product-grid__item') !== null ||
      currentProductsView.querySelector('results-list') !== null ||
      currentProductsView.querySelector('results-list .product-grid__item') !== null
    );
    
    const currentHasArticles = this.querySelector('[data-view="articles"] [data-articles-list] li') !== null ||
                               this.querySelector('[data-view="articles"] .search-results__article-item') !== null;
    const currentHasPages = this.querySelector('[data-view="pages"] [data-pages-list] li') !== null ||
                            this.querySelector('[data-view="pages"] .search-results__page-item') !== null;
    
    const hasProducts = fetchedHasProducts || currentHasProducts;
    const hasArticles = fetchedHasArticles || currentHasArticles;
    const hasPages = fetchedHasPages || currentHasPages;
    
    if (fetchedHasArticles) {
      const currentArticlesCount = parseInt(this.dataset.articlesCount || '0');
      const fetchedArticlesCount = parseInt(fetchedElement.dataset.articlesCount || '0');
      if (currentArticlesCount === 0 && fetchedArticlesCount > 0) {
        this.dataset.articlesCount = fetchedArticlesCount.toString();
      } else if (currentArticlesCount === 0 && fetchedHasArticles) {
        this.dataset.articlesCount = '1';
      }
    }
    if (fetchedHasPages) {
      const currentPagesCount = parseInt(this.dataset.pagesCount || '0');
      const fetchedPagesCount = parseInt(fetchedElement.dataset.pagesCount || '0');
      if (currentPagesCount === 0 && fetchedPagesCount > 0) {
        this.dataset.pagesCount = fetchedPagesCount.toString();
      } else if (currentPagesCount === 0 && fetchedHasPages) {
        this.dataset.pagesCount = '1';
      }
    }
    
    const finalHasArticles = parseInt(this.dataset.articlesCount || '0') > 0 || hasArticles;
    const finalHasPages = parseInt(this.dataset.pagesCount || '0') > 0 || hasPages;
    
    this.#updateButtonVisibility(buttonsContainer, 'products', hasProducts);
    this.#updateButtonVisibility(buttonsContainer, 'articles', finalHasArticles);
    this.#updateButtonVisibility(buttonsContainer, 'pages', finalHasPages);
  }

  getTranslation(key, params = {}) {
    const translations = (typeof Theme !== 'undefined' && Theme.translations) || {};
    
    // Handle nested keys like 'search.count'
    const keyParts = key.split('.');
    let translationValue = translations;
    for (const part of keyParts) {
      translationValue = translationValue && typeof translationValue === 'object' ? translationValue[part] : undefined;
    }
    
    let translation = typeof translationValue === 'string' ? translationValue : '';
    
    // Fallback to default English text if translation not found
    if (!translation) {
      translation = 'Showing {{ current }} of {{ total }} results';
    }
    
    // Replace placeholders with params
    Object.keys(params).forEach((paramKey) => {
      translation = translation.replace(`{{ ${paramKey} }}`, String(params[paramKey]));
    });
    
    return translation;
  }

  updateCountDisplays() {
    const totalResults = parseInt(this.dataset.totalResults || '0');
    
    const articlesCountEl = this.querySelector('[data-articles-count]');
    if (articlesCountEl) {
      const articlesList = this.querySelector('[data-articles-list]');
      const currentCount = articlesList ? articlesList.children.length : 0;
      articlesCountEl.textContent = this.getTranslation('search.count', {
        current: currentCount,
        total: totalResults
      });
    }
    
    const pagesCountEl = this.querySelector('[data-pages-count]');
    if (pagesCountEl) {
      const pagesList = this.querySelector('[data-pages-list]');
      const currentCount = pagesList ? pagesList.children.length : 0;
      pagesCountEl.textContent = this.getTranslation('search.count', {
        current: currentCount,
        total: totalResults
      });
    }
  }

  async switchView(viewType, updateURL = true) {
    if (!['products', 'articles', 'pages'].includes(viewType)) {
      return;
    }

    const allViewButtons = Array.from(
      this.querySelectorAll('.search-results__view-button')
    );
    
    allViewButtons.forEach((button) => {
      const isActive = button.dataset.viewType === viewType;
      button.setAttribute('aria-pressed', isActive.toString());
      
      if (isActive) {
        button.classList.add('active');
      } else {
        button.classList.remove('active');
      }
    });
    
    this.viewButtons = allViewButtons;
    this.currentViewType = viewType;

    const facetsToggle = this.querySelector('.facets-toggle');
    if (facetsToggle) {
      if (viewType === 'products') {
        facetsToggle.style.display = '';
        facetsToggle.removeAttribute('hidden');
      } else {
        facetsToggle.style.display = 'none';
        facetsToggle.setAttribute('hidden', '');
      }
    }

    Object.values(this.viewContainers).forEach((container) => {
      if (container) {
        container.setAttribute('hidden', '');
      }
    });

    const selectedView = this.viewContainers[viewType];
    if (selectedView) {
      selectedView.removeAttribute('hidden');
    }

    let hasItems = false;
    if (viewType === 'products') {
      const productGrid = selectedView?.querySelector('.product-grid__item');
      hasItems = !!productGrid;
    } else if (viewType === 'articles') {
      const articlesList = selectedView?.querySelector('[data-articles-list]');
      hasItems = articlesList && articlesList.children.length > 0;
    } else if (viewType === 'pages') {
      const pagesList = selectedView?.querySelector('[data-pages-list]');
      hasItems = pagesList && pagesList.children.length > 0;
    }

    if (viewType === 'articles' || viewType === 'pages') {
      const listSelector = viewType === 'articles' ? '[data-articles-list]' : '[data-pages-list]';
      const existingList = selectedView?.querySelector(listSelector);
      
      if (existingList && existingList.children.length > 0) {
        const emptyState = selectedView?.querySelector('.main-collection-grid__empty');
        if (emptyState) {
          emptyState.style.display = 'none';
        }
        this.#hideLoadingState(selectedView);
        return;
      }
      
      this.#showLoadingState(selectedView, viewType);
      
      const pageToLoad = 1;
      
      if (existingList) {
        existingList.innerHTML = '';
      }
      
      const totalResults = parseInt(this.dataset.totalResults || '0');
      const totalPages = parseInt(this.dataset.totalPages || '1');
      
      if (totalResults > 0 && totalPages > 0) {
        try {
          let currentList = selectedView?.querySelector(listSelector);
          
          if (!currentList) {
            const emptyState = selectedView?.querySelector('.main-collection-grid__empty');
            if (emptyState) {
              emptyState.style.display = 'none';
            }
            
            const listElement = document.createElement('ul');
            listElement.className = viewType === 'articles' 
              ? 'search-results__articles-list list-unstyled'
              : 'search-results__pages-list list-unstyled';
            listElement.setAttribute('data-' + viewType + '-list', '');
            
            if (selectedView) {
              selectedView.insertBefore(listElement, selectedView.firstChild);
              currentList = listElement;
            }
          }
          
          if (!currentList) {
            this.#hideLoadingState(selectedView);
            return;
          }
          
          if (currentList && currentList.children.length > 0) {
            this.#hideLoadingState(selectedView);
            return;
          }
          
          const pageUrl = new URL(window.location.href);
          pageUrl.searchParams.delete('page');
          pageUrl.searchParams.set('page', pageToLoad.toString());
          pageUrl.searchParams.delete('type');
          
          const sectionHTML = await sectionRenderer.getSectionHTML(
                this.dataset.sectionId,
                true,
            pageUrl
          );
          
                const parser = new DOMParser();
                const doc = parser.parseFromString(sectionHTML, 'text/html');
                const fetchedElement = doc.querySelector('search-results');
          
          let allItems = [];
                
                if (fetchedElement) {
                  const fetchedViewContainer = fetchedElement.querySelector(`[data-view="${viewType}"]`);
                  
                  if (fetchedViewContainer) {
                    const fetchedList = fetchedViewContainer.querySelector(listSelector);
                    
                    if (fetchedList && fetchedList.children.length > 0) {
                const items = Array.from(fetchedList.children).map(item => {
                  const cloned = item.cloneNode(true);
                  cloned.setAttribute('data-loaded-from-page', pageToLoad.toString());
                  return cloned;
                });
                allItems.push(...items);
              }
            }
          }
          
          if (allItems.length === 0) {
            const totalPages = parseInt(this.dataset.totalPages || '1');
            const pagesToCheck = Math.min(3, totalPages);
            
            for (let checkPage = 2; checkPage <= pagesToCheck; checkPage++) {
              const checkPageUrl = new URL(window.location.href);
              checkPageUrl.searchParams.delete('page');
              checkPageUrl.searchParams.set('page', checkPage.toString());
              checkPageUrl.searchParams.delete('type');
              
              const checkPageHTML = await sectionRenderer.getSectionHTML(
                this.dataset.sectionId,
                true,
                checkPageUrl
              );
              
              const checkPageDoc = parser.parseFromString(checkPageHTML, 'text/html');
              const checkPageElement = checkPageDoc.querySelector('search-results');
              
              if (checkPageElement) {
                const checkPageViewContainer = checkPageElement.querySelector(`[data-view="${viewType}"]`);
                
                if (checkPageViewContainer) {
                  const checkPageList = checkPageViewContainer.querySelector(listSelector);
                  
                  if (checkPageList && checkPageList.children.length > 0) {
                    const items = Array.from(checkPageList.children).map(item => {
                      const cloned = item.cloneNode(true);
                      cloned.setAttribute('data-loaded-from-page', checkPage.toString());
                      return cloned;
                    });
                    allItems.push(...items);
                    break;
                  }
                }
              }
            }
          }
          
          this.#hideLoadingState(selectedView);
          
          if (allItems.length > 0) {
            const emptyState = selectedView?.querySelector('.main-collection-grid__empty');
            if (emptyState) {
              emptyState.style.display = 'none';
            }
            
            currentList.innerHTML = '';
            allItems.forEach((item) => {
              currentList.appendChild(item);
            });

            const countElement = selectedView.querySelector('.search-results__count');
            if (countElement) {
              const currentCount = currentList.children.length;
              const totalResults = parseInt(this.dataset.totalResults || '0');
              countElement.textContent = this.getTranslation('search.count', {
                current: currentCount,
                total: totalResults
              });
            } else {
              const loadMoreContainer = selectedView.querySelector('.search-results__load-more');
              if (loadMoreContainer) {
                const countEl = document.createElement('div');
                countEl.className = 'main-search__count text-body-m-regular text-body-m-regular-desktop';
                countEl.setAttribute('data-' + viewType + '-count', '');
                const currentCount = currentList.children.length;
                const totalResults = parseInt(this.dataset.totalResults || '0');
                countEl.textContent = this.getTranslation('search.count', {
                  current: currentCount,
                  total: totalResults
                });
                loadMoreContainer.insertBefore(countEl, loadMoreContainer.firstChild);
              }
            }

            const loadMoreButton = selectedView.querySelector('.search-results__load-more-button');
            if (loadMoreButton && totalPages > 1) {
              loadMoreButton.style.display = '';
              loadMoreButton.disabled = false;
              loadMoreButton.dataset.currentPage = pageToLoad.toString();
            }
          } else {
            const emptyState = selectedView?.querySelector('.main-collection-grid__empty');
            if (emptyState) {
              emptyState.style.display = '';
            }
          }
        } catch (error) {
          console.error('[SearchResults] Error switching view:', {
            error,
            viewType,
            sectionId: this.dataset.sectionId,
            pageToLoad
          });
          this.#hideLoadingState(selectedView);
        }
      } else {
        this.#hideLoadingState(selectedView);
      }
    } else if (viewType === 'products' && hasItems) {
      this.#hideLoadingState(selectedView);
    }

    if (updateURL) {
      const url = new URL(window.location.href);
      url.searchParams.set('result_type', viewType);
      url.searchParams.delete('page');
      window.history.pushState({ result_type: viewType }, '', url.toString());
    }
  }

  #showLoadingState(viewContainer, viewType) {
    if (!viewContainer) return;
    
    const listSelector = viewType === 'articles' ? '[data-articles-list]' : '[data-pages-list]';
    const currentList = viewContainer.querySelector(listSelector);
    if (currentList) {
      currentList.style.display = 'none';
    }
    
    const emptyState = viewContainer.querySelector('.main-collection-grid__empty');
    if (emptyState) {
      emptyState.style.display = 'none';
    }
    
    let loadingState = viewContainer.querySelector('.search-results__loading');
    if (!loadingState) {
      loadingState = document.createElement('ul');
      loadingState.className = `search-results__loading search-results__${viewType}-list list-unstyled`;
      loadingState.setAttribute('aria-hidden', 'false');
      
      for (let i = 0; i < 4; i++) {
        const skeletonItem = document.createElement('li');
        skeletonItem.className = `search-results__${viewType}-item search-results__loading-item`;
        
        const skeletonLink = document.createElement('div');
        skeletonLink.className = viewType === 'articles' 
          ? 'search-results__article-link'
          : 'search-results__page-link';
        
        const skeletonContent = document.createElement('div');
        const skeletonTitle = document.createElement('div');
        skeletonTitle.className = `search-results__${viewType}-title search-results__loading-title`;
        skeletonTitle.style.cssText = 'height: 1.5rem; width: 60%; background: var(--color-neutral-lightest-grey); border-radius: 0.25rem; margin-block-end: 0.5rem;';
        
        const skeletonBody = document.createElement('div');
        skeletonBody.className = `search-results__${viewType}-body search-results__loading-body`;
        skeletonBody.style.cssText = 'height: 1rem; width: 100%; background: var(--color-neutral-lightest-grey); border-radius: 0.25rem; margin-block-end: 0.25rem;';
        
        const skeletonBody2 = document.createElement('div');
        skeletonBody2.style.cssText = 'height: 1rem; width: 80%; background: var(--color-neutral-lightest-grey); border-radius: 0.25rem;';
        
        skeletonContent.appendChild(skeletonTitle);
        skeletonContent.appendChild(skeletonBody);
        skeletonContent.appendChild(skeletonBody2);
        skeletonLink.appendChild(skeletonContent);
        skeletonItem.appendChild(skeletonLink);
        loadingState.appendChild(skeletonItem);
      }
      
      if (currentList && currentList.parentElement) {
        currentList.parentElement.insertBefore(loadingState, currentList);
      } else {
        viewContainer.insertBefore(loadingState, viewContainer.firstChild);
      }
    } else {
      loadingState.style.display = '';
      loadingState.removeAttribute('hidden');
    }
  }

  #hideLoadingState(viewContainer) {
    if (!viewContainer) return;
    
    const loadingState = viewContainer.querySelector('.search-results__loading');
    if (loadingState) {
      loadingState.style.display = 'none';
      loadingState.setAttribute('hidden', '');
      loadingState.setAttribute('aria-hidden', 'true');
    }
    
    const articlesList = viewContainer.querySelector('[data-articles-list]');
    const pagesList = viewContainer.querySelector('[data-pages-list]');
    if (articlesList) {
      articlesList.style.display = '';
    }
    if (pagesList) {
      pagesList.style.display = '';
    }
  }

  setupLoadMoreButtons() {
    const loadMoreButtons = Array.from(
      this.querySelectorAll('.search-results__load-more-button')
    );

    loadMoreButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleLoadMore(e.currentTarget);
      });
    });
  }

  async handleLoadMore(button) {
    const viewType = button.dataset.viewType;
    const currentPage = parseInt(button.dataset.currentPage || '1');
    const totalPages = parseInt(button.dataset.totalPages || '1');

    if (currentPage >= totalPages) {
      return;
    }

    // Store current scroll position before any async operations
    const scrollPosition = window.scrollY;

    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = 'Loading...';

    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      url.searchParams.set('page', (currentPage + 1).toString());
      url.searchParams.delete('type');

      const sectionHTML = await sectionRenderer.getSectionHTML(
        this.dataset.sectionId,
        true,
        url
      );

      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHTML, 'text/html');
      const fetchedElement = doc.querySelector('search-results');
      if (!fetchedElement) {
        throw new Error('Could not find search results element');
      }

      const listSelector = viewType === 'articles' ? '[data-articles-list]' : '[data-pages-list]';
      // Find the list within the correct view container
      const viewContainer = this.viewContainers[viewType];
      if (!viewContainer) {
        throw new Error(`Could not find view container for view type: ${viewType}`);
      }
      
      const currentList = viewContainer.querySelector(listSelector);
      const fetchedViewContainer = fetchedElement.querySelector(`[data-view="${viewType}"]`);
      const fetchedList = fetchedViewContainer ? fetchedViewContainer.querySelector(listSelector) : null;

      if (!currentList) {
        throw new Error(`Could not find current list container for view type: ${viewType}`);
      }
      if (!fetchedList) {
        // No items of this type on this page - hide button
        button.style.display = 'none';
        button.disabled = false;
        button.textContent = originalText;
        return;
      }

      const fetchedItems = Array.from(fetchedList.children);
      const newPage = currentPage + 1;
      
      const existingItemIds = new Set(
        Array.from(currentList.children).map(item => {
          const link = item.querySelector('a');
          return link ? link.getAttribute('href') : null;
        }).filter(Boolean)
      );
      
      let appendedCount = 0;
      fetchedItems.forEach((item) => {
        const link = item.querySelector('a');
        const itemId = link ? link.getAttribute('href') : null;
        
        if (itemId && !existingItemIds.has(itemId)) {
          const clonedItem = item.cloneNode(true);
          clonedItem.setAttribute('data-loaded-from-page', newPage.toString());
          currentList.appendChild(clonedItem);
          existingItemIds.add(itemId);
          appendedCount++;
        }
      });

      button.dataset.currentPage = newPage.toString();

      if (newPage >= totalPages) {
        button.style.display = 'none';
      } else {
        button.disabled = false;
        button.textContent = originalText;
      }

      // Check if products exist in the fetched page and load them if needed
      const fetchedProductsView = fetchedElement.querySelector('[data-view="products"]');
      if (fetchedProductsView) {
        const fetchedProductsList = fetchedProductsView.querySelector('results-list');
        if (fetchedProductsList) {
          const currentProductsView = this.viewContainers.products;
          if (currentProductsView) {
            let currentProductsList = currentProductsView.querySelector('results-list');
            if (!currentProductsList) {
              // Products view exists but no results-list yet - clone it from fetched
              const clonedProductsList = fetchedProductsList.cloneNode(true);
              currentProductsView.appendChild(clonedProductsList);
              currentProductsList = clonedProductsList;
            }
            
            // Merge products from fetched page into current
            if (currentProductsList) {
              const fetchedProductItems = fetchedProductsList.querySelectorAll('.product-grid__item');
              const existingProductIds = new Set(
                Array.from(
                  currentProductsList.querySelectorAll(
                    '.product-grid__item[data-product-id]'
                  )
                ).map(item => item.dataset.productId)
              );
              
              const grid = currentProductsList.querySelector('.product-grid, [class*="grid"], ul');
              fetchedProductItems.forEach((item) => {
                const productId = item.dataset.productId;
                if (productId && !existingProductIds.has(productId)) {
                  const clonedItem = item.cloneNode(true);
                  clonedItem.setAttribute('data-loaded-from-page', newPage.toString());
                  if (grid) {
                    grid.appendChild(clonedItem);
                  } else {
                    currentProductsList.appendChild(clonedItem);
                  }
                  existingProductIds.add(productId);
                }
              });
            }
          }
        }
      }
      
      // Update count display dynamically
      this.updateCountDisplays();
      
      // Update buttons from fetched page to detect newly loaded content types
      this.updateViewButtonsFromFetchedPage(fetchedElement);
      
      // Update from current page to ensure buttons reflect all loaded content
      this.updateViewButtonsFromCurrentPage();
      
      // Restore scroll position after content loads  
      requestAnimationFrame(() => {
        if (Math.abs(window.scrollY - scrollPosition) > 50) {
          window.scrollTo({
            top: scrollPosition,
            behavior: 'instant'
          });
        }
      });
    } catch (error) {
      console.error('[SearchResults] Error loading more results:', {
        error,
        viewType,
        currentPage,
        totalPages,
        sectionId: this.dataset.sectionId
      });
      
      // Restore scroll position even on error
      requestAnimationFrame(() => {
        if (Math.abs(window.scrollY - scrollPosition) > 50) {
          window.scrollTo({
            top: scrollPosition,
            behavior: 'instant'
          });
        }
      });
      
      button.disabled = false;
      button.textContent = originalText;
    }
  }
}

if (!customElements.get('search-results')) {
  customElements.define('search-results', SearchResults);
}
